"use client"

import React, { createContext, useContext, type ReactNode } from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"
import { ModalScrollLockScope } from "@/components/ui/modal-scroll-lock"
import { BLADE_WIDTHS, type BladeSize } from "@/lib/blade-stack"

/**
 * Drop-in replacement for the shadcn Dialog family that renders a right-side blade
 * instead of a centred modal. Existing dialogs switch by changing their import; the
 * open/onOpenChange contract, DialogHeader/Title/Description/Footer all keep working.
 * The blade size is read from `DialogContent`'s max-w-* class (max-w-lg → sm,
 * max-w-xl/2xl → md, wider → lg/xl) or set explicitly with `size`.
 */
const Dialog = SheetPrimitive.Root
const DialogTrigger = SheetPrimitive.Trigger
const DialogClose = SheetPrimitive.Close

const SizeContext = createContext<BladeSize>("md")

function sizeFromClassName(className?: string): BladeSize {
  if (!className) return "md"
  if (/max-w-(sm|md|lg)\b/.test(className)) return "sm"
  if (/max-w-(xl|2xl)\b/.test(className)) return "md"
  if (/max-w-(3xl|4xl)\b/.test(className)) return "lg"
  if (/max-w-(5xl|6xl|7xl|\[)/.test(className)) return "xl"
  return "md"
}

/**
 * Dialogs place their form between DialogHeader and DialogFooter as loose children.
 * In a fixed-height blade those children must scroll, so consecutive non-header,
 * non-footer children are wrapped in a padded DialogBody automatically.
 */
function groupBody(children: ReactNode): ReactNode {
  const out: ReactNode[] = []
  let body: ReactNode[] = []
  const flush = () => {
    if (body.length > 0) {
      out.push(<DialogBody key={`body-${out.length}`}>{body}</DialogBody>)
      body = []
    }
  }
  React.Children.forEach(children, (child) => {
    const type = React.isValidElement(child) ? child.type : null
    if (type === DialogHeader || type === DialogFooter || type === DialogBody) {
      flush()
      out.push(child)
    } else if (child !== null && child !== undefined && child !== false) {
      body.push(child)
    }
  })
  flush()
  return out
}

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> {
  size?: BladeSize
}

const DialogContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, DialogContentProps>(
  ({ className, children, size, ...props }, ref) => {
    const resolved = size ?? sizeFromClassName(className)
    return (
      <SheetPrimitive.Portal>
        <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-primary/25 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none" />
        <SheetPrimitive.Content
          ref={ref}
          className={cn(
            "fixed inset-y-0 right-0 z-50 flex h-full max-w-[96vw] flex-col bg-card text-card-foreground shadow-drawer",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right motion-reduce:animate-none"
          )}
          style={{ width: BLADE_WIDTHS[resolved] }}
          {...props}
        >
          <SizeContext.Provider value={resolved}>
            <ModalScrollLockScope>
              <div className="flex min-h-0 flex-1 flex-col">{groupBody(children)}</div>
            </ModalScrollLockScope>
          </SizeContext.Provider>
          <SheetPrimitive.Close
            className="absolute right-5 top-4 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </SheetPrimitive.Close>
        </SheetPrimitive.Content>
      </SheetPrimitive.Portal>
    )
  }
)
DialogContent.displayName = "BladeDialogContent"

/** Header band: sits above the scrolling body, leaves room for the close button */
const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("shrink-0 space-y-1 border-b border-card-border px-6 py-4 pr-14", className)} {...props} />
)
DialogHeader.displayName = "BladeDialogHeader"

/** Footer rule: pinned at the bottom, actions right-aligned like the Hub's Clear/Done row */
const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("mt-auto flex shrink-0 items-center justify-end gap-2 border-t border-[var(--th-menu-border)] px-6 py-3", className)} {...props} />
)
DialogFooter.displayName = "BladeDialogFooter"

const DialogTitle = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Title>, React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>>(
  ({ className, ...props }, ref) => (
    <SheetPrimitive.Title ref={ref} className={cn("font-display text-xl font-bold leading-tight tracking-tight text-foreground", className)} {...props} />
  )
)
DialogTitle.displayName = "BladeDialogTitle"

const DialogDescription = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Description>, React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>>(
  ({ className, ...props }, ref) => <SheetPrimitive.Description ref={ref} className={cn("text-xs text-muted-foreground", className)} {...props} />
)
DialogDescription.displayName = "BladeDialogDescription"

/**
 * Scrolling body for content placed between header and footer. Dialogs that render
 * bare children between DialogHeader and DialogFooter get this wrapper for free via
 * `DialogBody`; wrap explicitly when the content needs its own padding.
 */
const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("min-h-0 flex-1 overflow-y-auto px-6 py-4", className)} {...props} />
)
DialogBody.displayName = "BladeDialogBody"

export function useBladeDialogSize(): BladeSize {
  return useContext(SizeContext)
}

export { Dialog, DialogTrigger, DialogClose, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription, DialogBody }
